import React, { useEffect, useState } from 'react';
import './AlpacaStatus.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const fmtMoney = (n) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const fmtPct = (n) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  const v = Number(n) * 100;
  const sign = v >= 0 ? '+' : '';
  return `${sign}${v.toFixed(2)}%`;
};

const AlpacaStatus = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [lastAction, setLastAction] = useState(null);

  const load = async () => {
    try {
      const res = await fetch(`${API_URL}/api/alpaca/status`);
      const json = await res.json();
      if (json.status === 'success' && json.connected) {
        setData(json);
        setError(null);
      } else {
        setError(json.message || 'Alpaca connection failed');
      }
    } catch (e) {
      setError('Network error contacting backend');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 60000); // refresh every 60s
    return () => clearInterval(iv);
  }, []);

  const executeRotation = async (dryRun = false) => {
    setExecuting(true);
    setLastAction(null);
    try {
      const res = await fetch(
        `${API_URL}/api/alpaca/execute-rotation?dry_run=${dryRun}`,
        { method: 'POST' },
      );
      const json = await res.json();
      setLastAction(json);
      if (!dryRun) await load();
    } catch (e) {
      setLastAction({ status: 'error', message: 'Network error' });
    } finally {
      setExecuting(false);
    }
  };

  if (loading) {
    return (
      <div className="alpaca-tile alpaca-tile-loading" data-testid="alpaca-status-loading">
        <div className="alpaca-pulse" />
        <div className="alpaca-pulse-text">Loading live ledger…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alpaca-tile alpaca-tile-error" data-testid="alpaca-status-error">
        <div className="alpaca-tile-header">
          <span className="alpaca-label">FLIP FLOP · LIVE</span>
          <span className="alpaca-status-tag off">DISCONNECTED</span>
        </div>
        <div className="alpaca-error-msg">{error}</div>
      </div>
    );
  }

  const acct = data.account || {};
  const positions = data.positions || [];
  const clock = data.clock || {};
  const active = positions[0];
  const totalPL = positions.reduce((sum, p) => sum + (p.unrealized_pl || 0), 0);

  return (
    <div className="alpaca-tile" data-testid="alpaca-status">
      <div className="alpaca-tile-header">
        <div className="alpaca-label-group">
          <span className="alpaca-label">FLIP FLOP · LIVE</span>
          <span className="alpaca-paper-badge">PAPER</span>
        </div>
        <div className="alpaca-header-right">
          <span className={`alpaca-status-tag ${clock.is_open ? 'on' : 'closed'}`}>
            {clock.is_open ? 'MARKET OPEN' : 'MARKET CLOSED'}
          </span>
          <span className="alpaca-status-tag on">LIVE</span>
        </div>
      </div>

      <div className="alpaca-metrics">
        <div className="alpaca-metric">
          <div className="alpaca-metric-label">Capital Deployed</div>
          <div className="alpaca-metric-value" data-testid="alpaca-capital-deployed">
            {active ? fmtMoney(active.market_value) : fmtMoney(0)}
          </div>
          <div className="alpaca-metric-sub">
            {active
              ? `Cost basis ${fmtMoney(active.cost_basis)}`
              : 'All cash · awaiting signal'}
          </div>
        </div>

        <div className="alpaca-metric">
          <div className="alpaca-metric-label">Active Position</div>
          <div className="alpaca-metric-value alpaca-symbol" data-testid="alpaca-active-symbol">
            {active ? active.symbol : 'FLAT'}
          </div>
          <div className="alpaca-metric-sub">
            {active
              ? `${Number(active.qty).toFixed(2)} sh @ ${fmtMoney(active.avg_entry_price)}`
              : 'No open position'}
          </div>
        </div>

        <div className="alpaca-metric">
          <div className="alpaca-metric-label">Unrealized P/L</div>
          <div
            className={`alpaca-metric-value ${totalPL >= 0 ? 'up' : 'down'}`}
            data-testid="alpaca-unrealized-pl"
          >
            {totalPL >= 0 ? '+' : ''}
            {fmtMoney(totalPL)}
          </div>
          <div className="alpaca-metric-sub">
            {active && active.unrealized_plpc !== null && active.unrealized_plpc !== undefined
              ? `${fmtPct(active.unrealized_plpc)} on position`
              : '—'}
          </div>
        </div>
      </div>

      <div className="alpaca-actions">
        <button
          className="alpaca-btn alpaca-btn-primary"
          onClick={() => executeRotation(false)}
          disabled={executing}
          data-testid="alpaca-execute-rotation-btn"
        >
          {executing ? 'Executing…' : 'Execute Today\'s Signal'}
        </button>
        <button
          className="alpaca-btn alpaca-btn-secondary"
          onClick={() => executeRotation(true)}
          disabled={executing}
          data-testid="alpaca-dry-run-btn"
        >
          Dry Run
        </button>
        <a
          className="alpaca-btn alpaca-btn-link"
          href="/ffsystemlive"
          data-testid="alpaca-ledger-link"
        >
          View Full Ledger →
        </a>
      </div>

      {lastAction && (
        <div
          className={`alpaca-last-action ${lastAction.status === 'success' ? 'ok' : 'bad'}`}
          data-testid="alpaca-last-action"
        >
          {lastAction.status === 'success' ? (
            lastAction.dry_run ? (
              <>
                <strong>Dry run:</strong> Would rotate to{' '}
                <strong>{lastAction.would_rotate_to}</strong> (signal{' '}
                {lastAction.signal?.action} {lastAction.signal?.next_target}).
              </>
            ) : lastAction.rotation?.rotated ? (
              <>
                <strong>Rotation executed.</strong>{' '}
                {lastAction.rotation.previous_symbol
                  ? `${lastAction.rotation.previous_symbol} → `
                  : ''}
                <strong>{lastAction.rotation.target_symbol}</strong> —{' '}
                {fmtMoney(lastAction.rotation.notional_allocated)} notional.
              </>
            ) : (
              <>
                <strong>No rotation needed:</strong>{' '}
                {lastAction.rotation?.reason || 'no change'}.
              </>
            )
          ) : (
            <>Error: {lastAction.message}</>
          )}
        </div>
      )}

      <div className="alpaca-footer">
        Broker account <code>{acct.account_number}</code> · Equity {fmtMoney(acct.equity)} · Signal fires at 9:30 AM ET
      </div>
    </div>
  );
};

export default AlpacaStatus;
